import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  TrendingUp, Users, Building2, Activity, BookOpen, DollarSign, BarChart3, Globe
} from 'lucide-react';

export const AnalyticsSection = ({ api }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { setAnalytics(await api('/analytics')); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading || !analytics) return <div className="flex justify-center py-12"><Activity className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6" data-testid="platform-analytics">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Platform Analytics & Growth</h2>
          <p className="text-sm text-slate-500">Comprehensive metrics for reporting and publicity</p>
        </div>
        <Button variant="outline" onClick={load} className="gap-2"><Activity className="w-4 h-4" /> Refresh Data</Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-5 text-white">
          <p className="text-emerald-100 text-sm font-medium">Total Platform Revenue</p>
          <p className="text-3xl font-bold mt-1">KES {(analytics.total_revenue || 0).toLocaleString()}</p>
          <p className="text-emerald-200 text-xs mt-2 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> {analytics.revenue_growth >= 0 ? '+' : ''}{analytics.revenue_growth || 0}% vs last month
          </p>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-5 text-white">
          <p className="text-blue-100 text-sm font-medium">Active Tenants</p>
          <p className="text-3xl font-bold mt-1">{analytics.active_tenants || 0}</p>
          <p className="text-blue-200 text-xs mt-2 flex items-center gap-1"><Users className="w-3 h-3" /> {analytics.new_tenants_this_month || 0} new this month</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-5 text-white">
          <p className="text-purple-100 text-sm font-medium">Total Bookings</p>
          <p className="text-3xl font-bold mt-1">{(analytics.total_bookings || 0).toLocaleString()}</p>
          <p className="text-purple-200 text-xs mt-2 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> {analytics.bookings_growth >= 0 ? '+' : ''}{analytics.bookings_growth || 0}% growth</p>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl p-5 text-white">
          <p className="text-amber-100 text-sm font-medium">Properties Listed</p>
          <p className="text-3xl font-bold mt-1">{analytics.total_properties || 0}</p>
          <p className="text-amber-200 text-xs mt-2 flex items-center gap-1"><Building2 className="w-3 h-3" /> Avg {analytics.avg_properties_per_tenant || 0} per tenant</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-slate-200/60 p-6">
          <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-emerald-500" /> Monthly Growth Trend</h3>
          <div className="space-y-4">
            {(analytics.monthly_growth || []).slice(-6).map((month, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="text-xs text-slate-500 w-16">{month.month}</span>
                <div className="flex-1 bg-slate-100 rounded-full h-3 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full transition-all"
                    style={{ width: `${Math.min((month.revenue / (analytics.max_monthly_revenue || 1)) * 100, 100)}%` }} />
                </div>
                <span className="text-sm font-medium text-slate-700 w-24 text-right">KES {(month.revenue || 0).toLocaleString()}</span>
              </div>
            ))}
            {(!analytics.monthly_growth || analytics.monthly_growth.length === 0) && (
              <p className="text-slate-400 text-sm text-center py-4">No monthly data available yet</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200/60 p-6">
          <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-blue-500" /> Key Metrics</h3>
          <div className="space-y-4">
            {[
              { label: 'Avg Revenue Per Tenant', value: `KES ${(analytics.avg_revenue_per_tenant || 0).toLocaleString()}`, color: 'text-emerald-600' },
              { label: 'Avg Bookings Per Property', value: analytics.avg_bookings_per_property || 0, color: 'text-blue-600' },
              { label: 'Platform Occupancy Rate', value: `${analytics.avg_occupancy_rate || 0}%`, color: 'text-purple-600' },
              { label: 'Tenant Retention Rate', value: `${analytics.retention_rate || 0}%`, color: 'text-amber-600' },
              { label: 'Total Users (All Tenants)', value: analytics.total_users || 0, color: 'text-slate-600' },
            ].map((metric, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                <span className="text-sm text-slate-600">{metric.label}</span>
                <span className={`text-sm font-bold ${metric.color}`}>{metric.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {analytics.plan_distribution && (
        <div className="bg-white rounded-2xl border border-slate-200/60 p-6">
          <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><Globe className="w-5 h-5 text-purple-500" /> Tenant Distribution by Plan</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(analytics.plan_distribution || {}).map(([plan, count]) => (
              <div key={plan} className="bg-slate-50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-slate-800">{count}</p>
                <p className="text-xs text-slate-500 capitalize">{plan.replace(/_/g, ' ')}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200/60 p-6">
        <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><BookOpen className="w-5 h-5 text-rose-500" /> Top Performing Tenants</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-xs text-slate-500">
                <th className="text-left py-2 px-3">Tenant</th>
                <th className="text-center py-2 px-3">Properties</th>
                <th className="text-center py-2 px-3">Bookings</th>
                <th className="text-right py-2 px-3">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {(analytics.top_tenants || []).map((t, i) => (
                <tr key={i} className="border-b last:border-0 hover:bg-slate-50">
                  <td className="py-2 px-3 font-medium">{t.name}</td>
                  <td className="py-2 px-3 text-center">{t.properties}</td>
                  <td className="py-2 px-3 text-center">{t.bookings}</td>
                  <td className="py-2 px-3 text-right font-medium text-emerald-600">KES {(t.revenue || 0).toLocaleString()}</td>
                </tr>
              ))}
              {(!analytics.top_tenants || analytics.top_tenants.length === 0) && (
                <tr><td colSpan={4} className="py-8 text-center text-slate-400">No tenant data yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
